// useHFBData.js - HFB Data Hooks for Supabase
// Location: src/hooks/useHFBData.js

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { 
  requireSession
} from '../lib/supabaseAuthHelpers';

// Debug flag - OFF by default in production
const DEBUG_HFB = process.env.NODE_ENV !== 'production';
const ENABLE_REALTIME_HFB_DEFAULT = false;

function debugLog(...args) {
  if (DEBUG_HFB) {
    console.log('[HFB]', ...args);
  }
}

// Fix 1: debugError also respects DEBUG_HFB
function debugError(...args) {
  if (DEBUG_HFB) {
    console.error('[HFB]', ...args);
  }
}

// Fix 2: Helper to ensure project_id is BIGINT-safe numeric
function toProjectId(value) {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    var parsed = Number(value);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return null;
}

// Debounce hook
function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  const debouncedFn = useCallback(function() {
    var args = arguments;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(function() {
      callbackRef.current.apply(null, args);
    }, delay);
  }, [delay]);
  
  useEffect(() => {
    return function() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedFn;
}

// Normalize snake_case to camelCase
function normalizeAssignment(row) {
  return {
    id: row.id,
    weekId: row.week_id,
    userId: row.user_id,
    teamId: row.team_id
  };
}

function normalizeAllocation(row) {
  return {
    id: row.id,
    weekId: row.week_id,
    userId: row.user_id,
    projectId: row.project_id,
    percentage: row.percentage,
    notes: row.notes || ''
  };
}

// HOOK: useHFBWeek
export function useHFBWeek(weekId) {
  const [weekMetadata, setWeekMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setWeekMetadata({ weekId, exists: true });
      setIsLoading(false);
    }, 50);
    return () => clearTimeout(timer);
  }, [weekId]);

  return { weekMetadata, isLoading, error };
}

// HOOK: useHFBTeamAssignments
export function useHFBTeamAssignments(weekId) {
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);
  const lastWeekIdRef = useRef(null);

  const fetchAssignments = useCallback(async function(force) {
    if (!weekId) {
      setAssignments([]);
      return [];
    }
    
    if (isFetchingRef.current && !force) {
      return assignments;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      var response = await supabase
        .from('weekly_team_assignments')
        .select('id, week_id, user_id, team_id')
        .eq('week_id', weekId);

      if (response.error) {
        debugError('Fetch assignments error:', response.error);
        setError(response.error.message || 'Failed to fetch team assignments');
        setAssignments([]);
        return [];
      }

      var normalized = (response.data || []).map(normalizeAssignment);
      setAssignments(normalized);
      lastWeekIdRef.current = weekId;
      return normalized;
    } catch (err) {
      debugError('Fetch assignments error:', err);
      setError(err.message || 'Unexpected error fetching team assignments');
      setAssignments([]);
      return [];
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [weekId, assignments]);

  useEffect(function() {
    if (weekId && weekId !== lastWeekIdRef.current) {
      fetchAssignments(true);
    }
  }, [weekId]); // eslint-disable-line react-hooks/exhaustive-deps

  var refetch = useCallback(function() {
    isFetchingRef.current = false;
    return fetchAssignments(true);
  }, [fetchAssignments]);

  return { assignments, isLoading, error, refetch, setAssignments };
}

// HOOK: useHFBTeamAssignmentsForWeeks
export function useHFBTeamAssignmentsForWeeks(weekIds) {
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);
  const lastWeekIdsRef = useRef(null);

  const weekIdsKey = useMemo(function() {
    if (!weekIds || !Array.isArray(weekIds) || weekIds.length === 0) return '';
    return [...weekIds].sort().join(',');
  }, [weekIds]);

  const fetchAssignments = useCallback(async function(force) {
    if (!weekIdsKey) {
      setAssignments([]);
      return [];
    }
    
    if (isFetchingRef.current && !force) {
      return assignments;
    }

    if (!force && lastWeekIdsRef.current === weekIdsKey && assignments.length > 0) {
      return assignments;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      var uniqueWeekIds = weekIdsKey.split(',').filter(Boolean);
      
      var response = await supabase
        .from('weekly_team_assignments')
        .select('id, week_id, user_id, team_id')
        .in('week_id', uniqueWeekIds);

      if (response.error) {
        debugError('Fetch multi-week assignments error:', response.error);
        setError(response.error.message || 'Failed to fetch team assignments');
        setAssignments([]);
        return [];
      }

      var normalized = (response.data || []).map(normalizeAssignment);
      setAssignments(normalized);
      lastWeekIdsRef.current = weekIdsKey;
      return normalized;
    } catch (err) {
      debugError('Fetch multi-week assignments error:', err);
      setError(err.message || 'Unexpected error fetching team assignments');
      setAssignments([]);
      return [];
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [weekIdsKey, assignments]);

  useEffect(function() {
    if (weekIdsKey && weekIdsKey !== lastWeekIdsRef.current) {
      fetchAssignments(true);
    }
  }, [weekIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  var refetch = useCallback(function() {
    isFetchingRef.current = false;
    return fetchAssignments(true);
  }, [fetchAssignments]);

  return { assignments, isLoading, error, refetch };
}

// HOOK: useHFBProjectAllocations
export function useHFBProjectAllocations(weekId) {
  const [allocations, setAllocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);
  const lastWeekIdRef = useRef(null);

  const fetchAllocations = useCallback(async function(force) {
    if (!weekId) {
      setAllocations([]);
      return [];
    }
    
    if (isFetchingRef.current && !force) {
      return allocations;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      var response = await supabase
        .from('weekly_project_allocations')
        .select('id, week_id, user_id, project_id, percentage, notes')
        .eq('week_id', weekId);

      if (response.error) {
        debugError('Fetch allocations error:', response.error);
        setError(response.error.message || 'Failed to fetch project allocations');
        setAllocations([]);
        return [];
      }

      var normalized = (response.data || []).map(normalizeAllocation);
      setAllocations(normalized);
      lastWeekIdRef.current = weekId;
      return normalized;
    } catch (err) {
      debugError('Fetch allocations error:', err);
      setError(err.message || 'Unexpected error fetching project allocations');
      setAllocations([]);
      return [];
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [weekId, allocations]);

  useEffect(function() {
    if (weekId && weekId !== lastWeekIdRef.current) {
      fetchAllocations(true);
    }
  }, [weekId]); // eslint-disable-line react-hooks/exhaustive-deps

  var refetch = useCallback(function() {
    isFetchingRef.current = false;
    return fetchAllocations(true);
  }, [fetchAllocations]);

  return { allocations, isLoading, error, refetch, setAllocations };
}

// HOOK: useHFBProjectAllocationsForDonut
export function useHFBProjectAllocationsForDonut(weekStartDate) {
  const [allocations, setAllocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);

  var getWeekIdFromMonday = useCallback(function(mondayDateStr) {
    if (!mondayDateStr) return null;
    var d = new Date(mondayDateStr + 'T00:00:00Z');
    var dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    var weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return d.getUTCFullYear() + '-W' + String(weekNum).padStart(2, '0');
  }, []);

  var weekId = useMemo(function() { return getWeekIdFromMonday(weekStartDate); }, [weekStartDate, getWeekIdFromMonday]);

  var fetchAllocations = useCallback(async function() {
    if (!weekId) {
      setAllocations([]);
      return;
    }
    
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      var response = await supabase
        .from('weekly_project_allocations')
        .select('id, week_id, user_id, project_id, percentage, notes')
        .eq('week_id', weekId);

      if (response.error) {
        debugError('Fetch allocations for Donut error:', response.error);
        setError(response.error.message || 'Failed to fetch project allocations');
        setAllocations([]);
      } else {
        var normalized = (response.data || []).map(function(row) {
          return {
            id: row.id,
            userId: row.user_id,
            projectId: row.project_id,
            weekStart: weekStartDate,
            allocatedHours: (row.percentage / 100) * 40,
            percentage: row.percentage,
            notes: row.notes || ''
          };
        });
        setAllocations(normalized);
      }
    } catch (err) {
      debugError('Fetch allocations for Donut error:', err);
      setError(err.message || 'Unexpected error fetching project allocations');
      setAllocations([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [weekId, weekStartDate]);

  useEffect(function() {
    if (weekStartDate) {
      fetchAllocations();
    }
  }, [weekStartDate, fetchAllocations]);

  var refetch = useCallback(function() {
    isFetchingRef.current = false;
    fetchAllocations();
  }, [fetchAllocations]);

  return { allocations, isLoading, error, refetch, weekId };
}

// HOOK: useHFBRoleTargets
export function useHFBRoleTargets(weekId, projectIds) {
  const [targets, setTargets] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchKeyRef = useRef(null);

  var fetchTargets = useCallback(async function() {
    if (!weekId || !projectIds || projectIds.length === 0) {
      setTargets({});
      return {};
    }

    // projectIds must be numbers (BIGINT)
    var numericProjectIds = projectIds.map(function(id) {
      return typeof id === 'number' ? id : Number(id);
    }).filter(function(id) {
      return !isNaN(id);
    });

    if (numericProjectIds.length === 0) {
      setTargets({});
      return {};
    }

    var fetchKey = weekId + ':' + numericProjectIds.slice().sort(function(a, b) { return a - b; }).join(',');
    if (fetchKey === lastFetchKeyRef.current && Object.keys(targets).length > 0) {
      return targets;
    }

    setIsLoading(true);
    setError(null);

    try {
      var weekParts = weekId.split('-W');
      var year = Number(weekParts[0]);
      var week = Number(weekParts[1]);
      var jan4 = new Date(Date.UTC(year, 0, 4));
      var dayOfWeek = jan4.getUTCDay() || 7;
      var week1Monday = new Date(jan4);
      week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
      var targetMonday = new Date(week1Monday);
      targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
      var weekStart = targetMonday.toISOString().split('T')[0];

      var response = await supabase
        .from('weekly_project_role_targets')
        .select('id, week_start, project_id, role_initial, target_fte, updated_by, updated_at')
        .eq('week_start', weekStart)
        .in('project_id', numericProjectIds);

      if (response.error) {
        debugError('Fetch role targets error:', response.error);
        setError(response.error.message || 'Failed to fetch role targets');
        setTargets({});
        return {};
      }

      var normalizedTargets = {};
      (response.data || []).forEach(function(row) {
        var pid = String(row.project_id);
        if (!normalizedTargets[pid]) {
          normalizedTargets[pid] = {};
        }
        normalizedTargets[pid][row.role_initial] = {
          id: row.id,
          targetFte: row.target_fte,
          updatedBy: row.updated_by,
          updatedAt: row.updated_at
        };
      });

      setTargets(normalizedTargets);
      lastFetchKeyRef.current = fetchKey;
      return normalizedTargets;
    } catch (err) {
      debugError('Fetch role targets error:', err);
      setError(err.message || 'Unexpected error fetching role targets');
      setTargets({});
      return {};
    } finally {
      setIsLoading(false);
    }
  }, [weekId, projectIds, targets]);

  useEffect(function() {
    fetchTargets();
  }, [weekId, projectIds]); // eslint-disable-line react-hooks/exhaustive-deps

  var refetch = useCallback(function() {
    lastFetchKeyRef.current = null;
    return fetchTargets();
  }, [fetchTargets]);

  return { targets, isLoading, error, refetch, setTargets };
}

// HOOK: useHFBRoleTargetActions
export function useHFBRoleTargetActions() {
  
  var weekIdToWeekStart = function(weekId) {
    var weekParts = weekId.split('-W');
    var year = Number(weekParts[0]);
    var week = Number(weekParts[1]);
    var jan4 = new Date(Date.UTC(year, 0, 4));
    var dayOfWeek = jan4.getUTCDay() || 7;
    var week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
    var targetMonday = new Date(week1Monday);
    targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
    return targetMonday.toISOString().split('T')[0];
  };

  var toNumericProjectId = function(projectId) {
    if (typeof projectId === 'number') return projectId;
    var parsed = Number(projectId);
    if (isNaN(parsed)) {
      debugError('Invalid project ID:', projectId);
      return null;
    }
    return parsed;
  };

  // Fix 4: upsertRoleTarget uses user.id for updated_by
  // FIX: Correctly destructure { user } from requireSession() return value
  var upsertRoleTarget = async function(params) {
    var weekId = params.weekId;
    var projectId = params.projectId;
    var roleInitial = params.roleInitial;
    var targetFte = params.targetFte;
    // params.updatedBy is ignored; we use user.id instead

    if (!weekId || projectId === undefined || projectId === null || !roleInitial || targetFte === undefined) {
      return { success: false, error: 'Missing required parameters' };
    }

    var numericProjectId = toNumericProjectId(projectId);
    if (numericProjectId === null) {
      return { success: false, error: 'Invalid project ID: must be numeric' };
    }

    // FIX: Destructure { user } from requireSession() which returns { session, user }
    var user;
    try {
      var sessionResult = await requireSession();
      user = sessionResult.user;
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    var weekStart = weekIdToWeekStart(weekId);
    
    var payload = {
      week_start: weekStart,
      project_id: numericProjectId,
      role_initial: roleInitial.toUpperCase(),
      target_fte: parseFloat(targetFte) || 0,
      updated_by: user.id,
      updated_at: new Date().toISOString()
    };

    debugLog('upsertRoleTarget payload:', payload);

    try {
      var response = await supabase
        .from('weekly_project_role_targets')
        .upsert(payload, { onConflict: 'week_start,project_id,role_initial' })
        .select()
        .single();

      if (response.error) {
        debugError('upsertRoleTarget error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { 
        success: true, 
        data: {
          id: response.data.id,
          weekStart: response.data.week_start,
          projectId: response.data.project_id,
          roleInitial: response.data.role_initial,
          targetFte: response.data.target_fte,
          updatedBy: response.data.updated_by,
          updatedAt: response.data.updated_at
        }
      };
    } catch (err) {
      debugError('upsertRoleTarget error:', err);
      return { success: false, error: err.message };
    }
  };

  var deleteRoleTarget = async function(params) {
    var weekId = params.weekId;
    var projectId = params.projectId;
    var roleInitial = params.roleInitial;

    if (!weekId || projectId === undefined || projectId === null || !roleInitial) {
      return { success: false, error: 'Missing required parameters' };
    }

    var numericProjectId = toNumericProjectId(projectId);
    if (numericProjectId === null) {
      return { success: false, error: 'Invalid project ID: must be numeric' };
    }

    var weekStart = weekIdToWeekStart(weekId);

    debugLog('deleteRoleTarget:', { weekStart: weekStart, projectId: numericProjectId, roleInitial: roleInitial });

    try {
      var response = await supabase
        .from('weekly_project_role_targets')
        .delete()
        .eq('week_start', weekStart)
        .eq('project_id', numericProjectId)
        .eq('role_initial', roleInitial.toUpperCase());

      if (response.error) {
        debugError('deleteRoleTarget error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { success: true };
    } catch (err) {
      debugError('deleteRoleTarget error:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    upsertRoleTarget: upsertRoleTarget,
    deleteRoleTarget: deleteRoleTarget
  };
}

// HOOK: useHFBData (Combined hook)
export function useHFBData(weekId, enableRealtime) {
  if (enableRealtime === undefined) enableRealtime = ENABLE_REALTIME_HFB_DEFAULT;
  
  var assignmentsHook = useHFBTeamAssignments(weekId);
  var allocationsHook = useHFBProjectAllocations(weekId);
  
  var savingState = useState(false);
  var isSaving = savingState[0];
  var setIsSaving = savingState[1];
  var subscriptionsRef = useRef([]);

  var refreshWeekSnapshot = useCallback(async function() {
    var results = await Promise.all([
      assignmentsHook.refetch(),
      allocationsHook.refetch()
    ]);
    return results;
  }, [assignmentsHook.refetch, allocationsHook.refetch]);

  var debouncedRefresh = useDebouncedCallback(refreshWeekSnapshot, 300);

  useEffect(function() {
    if (!enableRealtime || !weekId) return;

    var assignmentsSub = supabase
      .channel('hfb-assignments-' + weekId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'weekly_team_assignments',
          filter: 'week_id=eq.' + weekId
        },
        function() {
          debouncedRefresh();
        }
      )
      .subscribe();

    var allocationsSub = supabase
      .channel('hfb-allocations-' + weekId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'weekly_project_allocations',
          filter: 'week_id=eq.' + weekId
        },
        function() {
          debouncedRefresh();
        }
      )
      .subscribe();

    subscriptionsRef.current = [assignmentsSub, allocationsSub];

    return function() {
      subscriptionsRef.current.forEach(function(sub) {
        supabase.removeChannel(sub);
      });
      subscriptionsRef.current = [];
    };
  }, [weekId, enableRealtime, debouncedRefresh]);

  return {
    assignments: assignmentsHook.assignments,
    assignmentsLoading: assignmentsHook.isLoading,
    assignmentsError: assignmentsHook.error,
    assignmentsRefetch: assignmentsHook.refetch,
    setLocalAssignments: assignmentsHook.setAssignments,
    
    allocations: allocationsHook.allocations,
    allocationsLoading: allocationsHook.isLoading,
    allocationsError: allocationsHook.error,
    allocationsRefetch: allocationsHook.refetch,
    setLocalAllocations: allocationsHook.setAllocations,
    
    isLoading: assignmentsHook.isLoading || allocationsHook.isLoading,
    errors: [assignmentsHook.error, allocationsHook.error].filter(Boolean),
    refreshWeekSnapshot: refreshWeekSnapshot,
    debouncedRefresh: debouncedRefresh,
    
    isSaving: isSaving,
    setIsSaving: setIsSaving
  };
}

// HOOK: useHFBActions
export function useHFBActions() {
  
  var assignTeam = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;
    var teamId = params.teamId;

    // RLS requires auth_user_id = auth.uid(), so use the current session user
    var sessionUser;
    try {
      var sessionResult = await requireSession();
      sessionUser = sessionResult.user;
    } catch (sessionErr) {
      debugError('assignTeam session error:', sessionErr);
      return { success: false, error: sessionErr.message };
    }

    var payload = {
      week_id: weekId,
      user_id: userId,
      team_id: teamId,
      auth_user_id: sessionUser.id
    };
    debugLog('assignTeam payload:', payload);

    try {
      var response = await supabase
        .from('weekly_team_assignments')
        .upsert(payload, { onConflict: 'week_id,user_id' })
        .select('id, week_id, user_id, team_id')
        .single();

      if (response.error) {
        debugError('assignTeam error:', response.error);
        var userMessage = response.error.message || 'Failed to assign team';
        if (response.error.code === '23503') {
          userMessage = 'Team ID ' + teamId + ' does not exist.';
        } else if (response.error.code === '42501') {
          userMessage = 'Permission denied.';
        }
        return { success: false, error: userMessage };
      }

      // Sync users.team_id so supervisor lookup always has current team
      try {
        await supabase.from('users').update({ team_id: teamId }).eq('id', userId);
      } catch (syncErr) {
        debugError('assignTeam: users.team_id sync failed (non-blocking):', syncErr);
      }

      return { success: true, data: normalizeAssignment(response.data) };
    } catch (err) {
      debugError('assignTeam error:', err);
      return { success: false, error: err.message || 'Unexpected error' };
    }
  };

  var removeTeamAssignment = async function(assignmentId) {
    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    try {
      var response = await supabase
        .from('weekly_team_assignments')
        .delete()
        .eq('id', assignmentId);

      if (response.error) {
        debugError('removeTeamAssignment error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { success: true };
    } catch (err) {
      debugError('removeTeamAssignment error:', err);
      return { success: false, error: err.message };
    }
  };

  var removeTeamAssignmentByUser = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;

    if (!weekId || userId === undefined || userId === null) {
      return { success: false, error: 'Missing weekId or userId' };
    }

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    try {
      var response = await supabase
        .from('weekly_team_assignments')
        .delete()
        .eq('week_id', weekId)
        .eq('user_id', userId);

      if (response.error) {
        debugError('removeTeamAssignmentByUser error:', response.error);
        return { success: false, error: response.error.message };
      }

      // Clear users.team_id when unassigning from team
      try {
        await supabase.from('users').update({ team_id: null }).eq('id', userId);
      } catch (syncErr) {
        debugError('removeTeamAssignmentByUser: users.team_id sync failed (non-blocking):', syncErr);
      }

      return { success: true };
    } catch (err) {
      debugError('removeTeamAssignmentByUser error:', err);
      return { success: false, error: err.message };
    }
  };

  var resetAllocationsForUser = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;

    if (!weekId || userId === undefined || userId === null) {
      return { success: false, error: 'Missing weekId or userId' };
    }

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    try {
      var response = await supabase
        .from('weekly_project_allocations')
        .delete()
        .eq('week_id', weekId)
        .eq('user_id', userId);

      if (response.error) {
        debugError('resetAllocationsForUser error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { success: true };
    } catch (err) {
      debugError('resetAllocationsForUser error:', err);
      return { success: false, error: err.message };
    }
  };

  var assignTeamAndResetAllocations = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;
    var teamId = params.teamId;
    var previousTeamId = params.previousTeamId;

    var isTeamChange = previousTeamId !== undefined && 
                       previousTeamId !== null && 
                       Number(previousTeamId) !== Number(teamId);
    
    if (isTeamChange) {
      var resetResult = await resetAllocationsForUser({ weekId: weekId, userId: userId });
      if (!resetResult.success) {
        return { success: false, error: 'Failed to reset allocations: ' + resetResult.error };
      }
    }

    return await assignTeam({ weekId: weekId, userId: userId, teamId: teamId });
  };

  var unassignFromTeamAndResetAllocations = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;

    var resetResult = await resetAllocationsForUser({ weekId: weekId, userId: userId });
    if (!resetResult.success) {
      debugLog('Warning: Failed to reset allocations:', resetResult.error);
    }
    return await removeTeamAssignmentByUser({ weekId: weekId, userId: userId });
  };

  // assignToFloating: move user to floating pool
  // Floating = no assignment row in DB (FK disallows null team_id)
  // Deletes the assignment row and resets allocations if user was on a team
  var assignToFloating = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;
    var previousTeamId = params.previousTeamId;

    if (!weekId || userId === undefined || userId === null) {
      return { success: false, error: 'Missing weekId or userId' };
    }

    debugLog('assignToFloating:', { weekId: weekId, userId: userId, previousTeamId: previousTeamId });

    // Reset allocations if user was on a team
    if (previousTeamId !== undefined && previousTeamId !== null) {
      var resetResult = await resetAllocationsForUser({ weekId: weekId, userId: userId });
      if (!resetResult.success) {
        debugLog('Warning: Failed to reset allocations during float:', resetResult.error);
      }
    }

    // Remove the team assignment row (floating = no row in DB)
    return await removeTeamAssignmentByUser({ weekId: weekId, userId: userId });
  };

  // Fix 2: addAllocation uses toProjectId helper
  var addAllocation = async function(params) {
    var weekId = params.weekId;
    var userId = params.userId;
    var projectId = params.projectId;
    var percentage = params.percentage;
    var notes = params.notes;

    // Fix 2: Validate and convert project_id to BIGINT-safe number
    var numericProjectId = toProjectId(projectId);
    if (numericProjectId === null) {
      return { success: false, error: 'Invalid project ID: must be numeric' };
    }

    // RLS requires auth_user_id = auth.uid(), so use the current session user
    var sessionUser;
    try {
      var sessionResult = await requireSession();
      sessionUser = sessionResult.user;
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }
    
    var payload = {
      week_id: weekId,
      user_id: userId,
      project_id: numericProjectId,
      percentage: percentage,
      notes: (notes || '').trim(),
      auth_user_id: sessionUser.id
    };
    debugLog('addAllocation payload:', payload);

    try {
      var response = await supabase
        .from('weekly_project_allocations')
        .insert(payload)
        .select()
        .single();

      if (response.error) {
        debugError('addAllocation error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { success: true, data: normalizeAllocation(response.data) };
    } catch (err) {
      debugError('addAllocation error:', err);
      return { success: false, error: err.message };
    }
  };

  var updateAllocation = async function(allocationId, updates) {
    if (!allocationId) {
      return { success: false, error: 'Missing allocation ID' };
    }

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    var dbUpdates = {};
    if (updates.percentage !== undefined) dbUpdates.percentage = updates.percentage;
    if (updates.notes !== undefined) dbUpdates.notes = (updates.notes || '').trim();

    if (Object.keys(dbUpdates).length === 0) {
      return { success: false, error: 'No updates provided' };
    }

    debugLog('updateAllocation:', { allocationId: allocationId, updates: dbUpdates });

    try {
      var response = await supabase
        .from('weekly_project_allocations')
        .update(dbUpdates)
        .eq('id', allocationId)
        .select()
        .single();

      if (response.error) {
        debugError('updateAllocation error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { success: true, data: normalizeAllocation(response.data) };
    } catch (err) {
      debugError('updateAllocation error:', err);
      return { success: false, error: err.message };
    }
  };

  var removeAllocation = async function(allocationId) {
    if (!allocationId) {
      return { success: false, error: 'Missing allocation ID' };
    }

    try {
      await requireSession();
    } catch (sessionErr) {
      return { success: false, error: sessionErr.message };
    }

    try {
      var response = await supabase
        .from('weekly_project_allocations')
        .delete()
        .eq('id', allocationId);

      if (response.error) {
        debugError('removeAllocation error:', response.error);
        return { success: false, error: response.error.message };
      }

      return { success: true };
    } catch (err) {
      debugError('removeAllocation error:', err);
      return { success: false, error: err.message };
    }
  };

  var weekIdToWeekStart = function(weekId) {
    var weekParts = weekId.split('-W');
    var year = Number(weekParts[0]);
    var week = Number(weekParts[1]);
    var jan4 = new Date(Date.UTC(year, 0, 4));
    var dayOfWeek = jan4.getUTCDay() || 7;
    var week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
    var targetMonday = new Date(week1Monday);
    targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
    return targetMonday.toISOString().split('T')[0];
  };

  var copyWeek = async function(sourceWeekId, targetWeekId) {
    debugLog('copyWeek:', { sourceWeekId: sourceWeekId, targetWeekId: targetWeekId });
    
    var result = { success: true, copiedTeams: 0, copiedAllocations: 0, copiedRoleTargets: 0, errors: [] };

    try {
      // RLS requires auth_user_id = auth.uid() on all inserts
      var copySessionResult = await requireSession();
      var copyAuthUid = copySessionResult.user.id;
    } catch (sessionErr) {
      return { success: false, copiedTeams: 0, copiedAllocations: 0, copiedRoleTargets: 0, errors: [sessionErr.message] };
    }

    try {
      var assignResponse = await supabase
        .from('weekly_team_assignments')
        .select('user_id, team_id')
        .eq('week_id', sourceWeekId);

      if (assignResponse.error) result.errors.push('Fetch assignments: ' + assignResponse.error.message);
      var sourceAssignments = assignResponse.data || [];

      var allocResponse = await supabase
        .from('weekly_project_allocations')
        .select('user_id, project_id, percentage, notes')
        .eq('week_id', sourceWeekId);

      if (allocResponse.error) result.errors.push('Fetch allocations: ' + allocResponse.error.message);
      var sourceAllocations = allocResponse.data || [];

      var sourceWeekStart = weekIdToWeekStart(sourceWeekId);
      var targetWeekStart = weekIdToWeekStart(targetWeekId);
      
      var roleTargetsResponse = await supabase
        .from('weekly_project_role_targets')
        .select('project_id, role_initial, target_fte')
        .eq('week_start', sourceWeekStart);

      if (roleTargetsResponse.error) result.errors.push('Fetch role targets: ' + roleTargetsResponse.error.message);
      var sourceRoleTargets = roleTargetsResponse.data || [];

      if (sourceAssignments.length > 0) {
        var targets = sourceAssignments.map(function(a) {
          return { 
            week_id: targetWeekId, 
            user_id: a.user_id, 
            team_id: a.team_id,
            auth_user_id: copyAuthUid
          };
        });

        var upsertResponse = await supabase
          .from('weekly_team_assignments')
          .upsert(targets, { onConflict: 'week_id,user_id' })
          .select();

        if (upsertResponse.error) result.errors.push('Copy assignments: ' + upsertResponse.error.message);
        else result.copiedTeams = upsertResponse.data ? upsertResponse.data.length : 0;
      }

      // Fix 2: copyWeek allocation payload uses toProjectId helper
      if (sourceAllocations.length > 0) {
        var allocTargets = [];
        var skippedAllocations = 0;
        
        sourceAllocations.forEach(function(a) {
          var numericProjectId = toProjectId(a.project_id);
          if (numericProjectId !== null) {
            allocTargets.push({ 
              week_id: targetWeekId, 
              user_id: a.user_id, 
              project_id: numericProjectId,
              percentage: a.percentage, 
              notes: a.notes || '',
              auth_user_id: copyAuthUid
            });
          } else {
            skippedAllocations++;
          }
        });

        if (skippedAllocations > 0) {
          result.errors.push('Skipped ' + skippedAllocations + ' allocations (invalid project_id)');
        }

        if (allocTargets.length > 0) {
          var allocUpsertResponse = await supabase
            .from('weekly_project_allocations')
            .upsert(allocTargets, { onConflict: 'week_id,user_id,project_id' })
            .select();

          if (allocUpsertResponse.error) {
            var userIds = [];
            allocTargets.forEach(function(a) {
              if (userIds.indexOf(a.user_id) === -1) userIds.push(a.user_id);
            });
            
            await supabase.from('weekly_project_allocations').delete()
              .eq('week_id', targetWeekId).in('user_id', userIds);
              
            var insertResponse = await supabase
              .from('weekly_project_allocations').insert(allocTargets).select();
              
            if (insertResponse.error) result.errors.push('Insert allocations: ' + insertResponse.error.message);
            else result.copiedAllocations = insertResponse.data ? insertResponse.data.length : 0;
          } else {
            result.copiedAllocations = allocUpsertResponse.data ? allocUpsertResponse.data.length : 0;
          }
        }
      }

      if (sourceRoleTargets && sourceRoleTargets.length > 0) {
        var roleTargetPayloads = sourceRoleTargets.map(function(rt) {
          return {
            week_start: targetWeekStart,
            project_id: rt.project_id,
            role_initial: rt.role_initial,
            target_fte: rt.target_fte,
            updated_at: new Date().toISOString()
          };
        });

        var roleTargetsUpsertResponse = await supabase
          .from('weekly_project_role_targets')
          .upsert(roleTargetPayloads, { onConflict: 'week_start,project_id,role_initial' })
          .select();

        if (roleTargetsUpsertResponse.error) {
          var projectIds = [];
          sourceRoleTargets.forEach(function(rt) {
            if (projectIds.indexOf(rt.project_id) === -1) projectIds.push(rt.project_id);
          });

          await supabase
            .from('weekly_project_role_targets')
            .delete()
            .eq('week_start', targetWeekStart)
            .in('project_id', projectIds);

          var roleTargetsInsertResponse = await supabase
            .from('weekly_project_role_targets')
            .insert(roleTargetPayloads)
            .select();

          if (roleTargetsInsertResponse.error) {
            result.errors.push('Insert role targets: ' + roleTargetsInsertResponse.error.message);
          } else {
            result.copiedRoleTargets = roleTargetsInsertResponse.data ? roleTargetsInsertResponse.data.length : 0;
          }
        } else {
          result.copiedRoleTargets = roleTargetsUpsertResponse.data ? roleTargetsUpsertResponse.data.length : 0;
        }
      }

      result.success = result.errors.length === 0;
      return result;

    } catch (err) {
      debugError('copyWeek error:', err);
      return { success: false, copiedTeams: 0, copiedAllocations: 0, copiedRoleTargets: 0, errors: [err.message] };
    }
  };

  return {
    assignTeam: assignTeam,
    removeTeamAssignment: removeTeamAssignment,
    removeTeamAssignmentByUser: removeTeamAssignmentByUser,
    assignTeamAndResetAllocations: assignTeamAndResetAllocations,
    unassignFromTeamAndResetAllocations: unassignFromTeamAndResetAllocations,
    assignToFloating: assignToFloating,
    resetAllocationsForUser: resetAllocationsForUser,
    addAllocation: addAllocation,
    updateAllocation: updateAllocation,
    removeAllocation: removeAllocation,
    copyWeek: copyWeek
  };
}

// ============================================================================
// JOB_ROLE → ROLE_INITIAL MAPPING
// ============================================================================
var JOB_ROLE_TO_INITIAL = {
  'Intern': 'I',
  'Junior Architect': 'J',
  'Medior Architect': 'M',
  'Project Architect': 'P',
  'Senior Architect': 'S',
  'Associate': 'A',
  'Director': 'D',
  'Partner': 'PA'
};

export function jobRoleToInitial(jobRole) {
  return JOB_ROLE_TO_INITIAL[jobRole] || null;
}

// ============================================================================
// HOOK: useHFBModelDemand
// Fetches studio-level model demand from capacity_gap_monthly and converts
// monthly FTE to weekly FTE per role_initial.
//
// Smart run selection: iterates forecast_runs (newest first) and picks the
// first one that actually has rows in capacity_gap_monthly.
//
// Only fetches when enabled=true. Returns:
//   demand:    { [weekStart]: { [roleInitial]: demandFte } }
//   runInfo:   { id, created_at, horizon_months, status } | null
//   noData:    true when no run has demand rows (not an error)
//   error:     non-null only for real Supabase errors
// ============================================================================
export function useHFBModelDemand(enabled, weekStarts) {
  var demandState = useState({});
  var demand = demandState[0];
  var setDemand = demandState[1];

  var loadingState = useState(false);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var noDataState = useState(false);
  var noData = noDataState[0];
  var setNoData = noDataState[1];

  var runInfoState = useState(null);
  var runInfo = runInfoState[0];
  var setRunInfo = runInfoState[1];

  var lastFetchKeyRef = useRef(null);

  // Stable key for weekStarts array
  var weekStartsKey = useMemo(function() {
    if (!weekStarts || weekStarts.length === 0) return '';
    return weekStarts.slice().sort().join(',');
  }, [weekStarts]);

  var fetchDemand = useCallback(async function() {
    if (!enabled || !weekStartsKey) {
      setDemand({});
      setNoData(false);
      setRunInfo(null);
      return;
    }

    var fetchKey = weekStartsKey;
    if (fetchKey === lastFetchKeyRef.current && Object.keys(demand).length > 0) {
      return; // already fetched for this window
    }

    setIsLoading(true);
    setError(null);
    setNoData(false);

    try {
      // 1. Get recent forecast runs (newest first, check up to 5)
      var runsRes = await supabase
        .from('forecast_runs')
        .select('id, horizon_months, created_at, status')
        .order('created_at', { ascending: false })
        .limit(5);

      if (runsRes.error) {
        debugError('useHFBModelDemand forecast_runs error:', runsRes.error.message, runsRes.error.code);
        throw runsRes.error;
      }

      var runs = runsRes.data || [];
      if (runs.length === 0) {
        setDemand({});
        setNoData(true);
        setRunInfo(null);
        lastFetchKeyRef.current = fetchKey;
        return;
      }

      // 2. Derive unique first-of-month dates (YYYY-MM-01) from weekStarts
      //    weekStarts are ISO date strings like "2026-02-17"
      var weekDates = weekStartsKey.split(',');
      var monthSet = {};
      weekDates.forEach(function(ws) {
        // Parse as UTC to avoid timezone shifts
        var d = new Date(ws + 'T00:00:00Z');
        var y = d.getUTCFullYear();
        var m = d.getUTCMonth(); // 0-based
        // Format as YYYY-MM-01
        var ms = y + '-' + String(m + 1).padStart(2, '0') + '-01';
        monthSet[ms] = true;
      });
      var months = Object.keys(monthSet);

      debugLog('useHFBModelDemand: weekDates=', weekDates, 'months=', months);

      // 3. Iterate runs and find first with actual demand rows
      var chosenRun = null;
      var rows = [];

      for (var i = 0; i < runs.length; i++) {
        var candidateRun = runs[i];
        var gapRes = await supabase
          .from('capacity_gap_monthly')
          .select('month_start, job_role, demand_p80_fte_weeks')
          .eq('forecast_run_id', candidateRun.id)
          .in('month_start', months);

        if (gapRes.error) {
          debugError('useHFBModelDemand capacity_gap_monthly error:', gapRes.error.message, gapRes.error.code);
          throw gapRes.error;
        }

        if (gapRes.data && gapRes.data.length > 0) {
          chosenRun = candidateRun;
          rows = gapRes.data;
          break;
        }
      }

      if (!chosenRun || rows.length === 0) {
        debugLog('useHFBModelDemand: no runs had demand rows for months', months);
        setDemand({});
        setNoData(true);
        setRunInfo(null);
        lastFetchKeyRef.current = fetchKey;
        return;
      }

      debugLog('useHFBModelDemand: using run', chosenRun.id, 'with', rows.length, 'demand rows');

      setRunInfo({
        id: chosenRun.id,
        created_at: chosenRun.created_at,
        horizon_months: chosenRun.horizon_months,
        status: chosenRun.status
      });

      // 4. Build monthly demand by role_initial
      //    Normalize month_start from Supabase (could be "2026-02-01" or
      //    "2026-02-01T00:00:00+00:00") to consistent YYYY-MM-DD
      var monthlyByRole = {};
      rows.forEach(function(row) {
        // Normalize: take first 10 chars to get YYYY-MM-DD
        var ms = String(row.month_start || '').substring(0, 10);
        var initial = jobRoleToInitial(row.job_role);
        if (!initial || !ms) return;

        if (!monthlyByRole[ms]) monthlyByRole[ms] = {};

        // Weeks in month: days / 7
        var d = new Date(ms + 'T00:00:00Z');
        var daysInMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
        var weeksInMonth = daysInMonth / 7;

        var ftePerWeek = (row.demand_p80_fte_weeks || 0) / weeksInMonth;

        if (!monthlyByRole[ms][initial]) monthlyByRole[ms][initial] = 0;
        monthlyByRole[ms][initial] += ftePerWeek;
      });

      // 5. Map each weekStart to its month's demand
      var result = {};
      weekDates.forEach(function(ws) {
        var d = new Date(ws + 'T00:00:00Z');
        var y = d.getUTCFullYear();
        var m = d.getUTCMonth();
        var ms = y + '-' + String(m + 1).padStart(2, '0') + '-01';
        result[ws] = monthlyByRole[ms] || {};
      });

      setDemand(result);
      setNoData(false);
      lastFetchKeyRef.current = fetchKey;
    } catch (err) {
      debugError('useHFBModelDemand fetch error:', err.message || err, err.code || '');
      setError(err.message || 'Failed to fetch model demand');
      setDemand({});
      setRunInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, weekStartsKey, demand]);

  useEffect(function() {
    if (enabled) {
      fetchDemand();
    } else {
      setDemand({});
      setNoData(false);
      setRunInfo(null);
      lastFetchKeyRef.current = null;
    }
  }, [enabled, weekStartsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  var refetch = useCallback(function() {
    lastFetchKeyRef.current = null;
    return fetchDemand();
  }, [fetchDemand]);

  return { demand: demand, isLoading: isLoading, error: error, noData: noData, runInfo: runInfo, refetch: refetch };
}

// HOOK: useUserProjectHistory
// Fetches the distinct set of project IDs the user has ever logged time on.
// Used by DonutTimeTrackerPage to keep previously-worked-on projects visible
// in the picker even when weekly allocations change.
export function useUserProjectHistory(userId) {
  const [projectIds, setProjectIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingRef = useRef(false);

  const fetchProjectHistory = useCallback(async () => {
    if (!userId) {
      setProjectIds(new Set());
      return;
    }
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select('project_id')
        .eq('user_id', userId);

      if (error) {
        debugError('Fetch user project history error:', error);
        setProjectIds(new Set());
      } else {
        setProjectIds(new Set((data || []).map(row => String(row.project_id))));
      }
    } catch (err) {
      debugError('Fetch user project history error:', err);
      setProjectIds(new Set());
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    fetchProjectHistory();
  }, [fetchProjectHistory]);

  const refetch = useCallback(() => {
    isFetchingRef.current = false;
    return fetchProjectHistory();
  }, [fetchProjectHistory]);

  return { projectIds, isLoading, refetch };
}
